import { FC, MouseEvent, ReactNode, useMemo, useState } from "react";
import { Fade, Spinner, Table } from "react-bootstrap";
import { Calendar4Event } from "react-bootstrap-icons";

import { fetchTrackingEvents } from "src/api/trackingEvents.ts";
import { containerColumns } from "src/components/containerList/containerListColumns.tsx";
import { usePaginationContext } from "src/components/containerList/containerListPaginationContext.ts";
import { CopyableText } from "src/components/copyableText.tsx";
import { TrackingEventList } from "src/components/subscriptionList/trackingEventList.tsx";
import { HeaderColumn } from "src/components/table/headerColumn.tsx";
import { ContainerSortBy } from "src/model/container.ts";
import { Container } from "src/model/subscription.ts";
import { useDialog, useToasts, useUserState } from "src/store/store.ts";
import { buildPortString } from "src/utils/ports.ts";
import { formatDateTime } from "src/utils/timeFormat.ts";

import styles from "src/components/subscriptionList/containerList.module.scss";

interface ContainerListProps {
  containers: Container[];
  columns: (keyof ContainerRow)[];
}

export interface ContainerRow {
  containerNumber: string;
  subscriptionId: string;
  containerNumberNode: ReactNode;
  detentionDemurragePrecarriageDays?: string;
  detentionDemurrageOncarriageDays?: string;
  lastStatus: string | null;
  lastStatusLocation: string | null;
  lastStatusDate: string;
  billOfLading: ReactNode;
  bookingNumber: ReactNode;
  externalReference: ReactNode;
  shippersReference: ReactNode;
  portOfLoading: string | null;
  etdPortOfLoading: string | null;
  portOfDischarge: string | null;
  etaPortOfDischarge: string | null;
  shippingLine: string;
  daysOfDelay: number | null;
  etaPlaceOfDelivery: string | null;
  terminalInformation: string | null;
}

export const ContainerList: FC<ContainerListProps> = ({ containers, columns }) => {
  const user = useUserState();
  const { showDialog } = useDialog();
  const { showToast } = useToasts();
  const { pagination, setPagination } = usePaginationContext();
  const [loading, setLoading] = useState<boolean>(false);

  const containerRows: ContainerRow[] = useMemo(() => {
    return containers.map(
      (c): ContainerRow => ({
        containerNumber: c.containerNumber,
        subscriptionId: c.subscriptionId,
        containerNumberNode: <CopyableText text={c.containerNumber} />,
        lastStatus: c.lastStatus?.status ?? null,
        lastStatusLocation: buildPortString(c.lastStatus?.locationName ?? null, c.lastStatus?.locationUnlocode ?? null),
        lastStatusDate: formatDateTime(c.lastStatus?.time),
        detentionDemurragePrecarriageDays: c.detentionDemurragePrecarriageDays?.toString(),
        detentionDemurrageOncarriageDays: c.detentionDemurrageOncarriageDays?.toString(),
        billOfLading: <CopyableText text={c.billOfLading} />,
        bookingNumber: <CopyableText text={c.bookingNumber} />,
        portOfLoading: buildPortString(c.portOfLoadingUnlocodeName, c.portOfLoadingUnlocode),
        portOfDischarge: buildPortString(c.portOfDischargeUnlocodeName, c.portOfDischargeUnlocode),
        shippersReference: <CopyableText text={c.shippersReference} />,
        externalReference: <CopyableText text={c.externalReference} />,
        etaPortOfDischarge: formatDateTime(c.etaPortOfDischarge),
        etdPortOfLoading: formatDateTime(c.etdPortOfLoading),
        shippingLine: `${c.shippingLineName} (${c.scac})`,
        daysOfDelay: c.daysOfDelay,
        terminalInformation: c.terminalInformation?.facilityName ?? null,
        etaPlaceOfDelivery: formatDateTime(c.etaPlaceOfDelivery),
      })
    );
  }, [containers]);

  const onSortChange = (sortKey: ContainerSortBy | undefined) => {
    if (sortKey === undefined) {
      return;
    }

    if (pagination.sortBy === sortKey) {
      setPagination((prevState) => ({
        ...prevState,
        pagination: { ...prevState.pagination, sortDir: prevState.pagination.sortDir === "Asc" ? "Desc" : "Asc" },
      }));
    } else {
      setPagination((prevState) => ({
        ...prevState,
        pagination: { ...prevState.pagination, sortBy: sortKey, sortDir: "Asc" },
      }));
    }
  };

  const showTrackingEvents = async (event: MouseEvent<SVGElement>, subscriptionId: string, containerNumber: string) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      setLoading(true);
      const trackingEvents = await fetchTrackingEvents(subscriptionId, containerNumber);
      showDialog(
        "Tracking Events for Container " + containerNumber,
        <TrackingEventList trackingEvents={trackingEvents} />,
        trackingEvents.length === 0 ? "lg" : "xl"
      );
    } catch {
      showToast("Failed to fetch tracking events", "danger");
    } finally {
      setLoading(false);
    }
  };

  if (containers.length === 0) {
    return <div>No containers found.</div>;
  }

  const orderedAndFilteredColumns = containerColumns
    .filter((cc) => columns.includes(cc.key))
    .toSorted((a, b) => columns.indexOf(a.key) - columns.indexOf(b.key));

  return (
    <>
      <Fade in={loading} unmountOnExit={true} mountOnEnter={true}>
        <div className={`d-flex justify-content-center align-items-center ${styles.block}`}>
          <div className={`w-100 h-100 ${styles.fade}`}></div>
          <Spinner />
        </div>
      </Fade>
      <Table>
        <thead>
          <tr>
            {orderedAndFilteredColumns
              .filter((cc) => columns.indexOf(cc.key) >= 0)
              .map((c) => (
                <th key={`th-${c.key}`}>
                  <HeaderColumn
                    value={c.value}
                    sortKey={c.sortKey}
                    onSortChange={onSortChange}
                    selectedSortKey={pagination.sortBy}
                    selectedSortDir={pagination.sortDir}
                  />
                </th>
              ))}
            {user.role === "Admin" ? <th style={{ width: "32px" }} className="text-end" /> : null}
          </tr>
        </thead>
        <tbody className="table-group-divider">
          {containerRows.map((cr, index) => (
            <tr key={`tr-${index}`}>
              {orderedAndFilteredColumns
                .filter((cc) => columns.indexOf(cc.key) >= 0)
                .map((column) => (
                  <td key={`td-${column.key}`}>{cr[column.key]}</td>
                ))}
              {user.role === "Admin" ? (
                <td className="text-end">
                  <Calendar4Event
                    onClick={(event) => showTrackingEvents(event, cr.subscriptionId, cr.containerNumber)}
                    role="button"
                  />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};
